import React from 'react';
import * as Sentry from '@sentry/react';
import { createContext, useContext, type ReactNode, useMemo } from 'react';

export interface UserContext {
  id: string;
  email?: string;
  username?: string;
}

export interface Logger {
  error(
    message: string,
    error?: unknown,
    metadata?: {
      action?: string;
      payload?: unknown;
      errors?: unknown[];
    }
  ): void;
  debug(message: string, ...args: unknown[]): void;
  setUser(user: UserContext | null): void;
  clearUser(): void;
}

export class ActionLogger implements Logger {
  error(
    message: string,
    error?: unknown,
    metadata?: {
      action?: string;
      payload?: unknown;
      errors?: unknown[];
    }
  ): void {
    // Always log to console.error
    // eslint-disable-next-line no-console
    console.error(message, error);

    // Also send to Sentry if available
    const sentryExtra: Record<string, unknown> = {
      message,
    };

    if (metadata) {
      if (metadata['action']) {
        sentryExtra['action'] = metadata['action'];
      }
      if (metadata['payload']) {
        sentryExtra['payload'] = metadata['payload'];
      }
      if (metadata['errors']) {
        sentryExtra['errors'] = metadata['errors'];
      }
    }

    const sentryTags: Record<string, string> = {};
    if (metadata?.['action']) {
      sentryTags['actionType'] = metadata['action'];
    }

    if (error) {
      Sentry.captureException(error, {
        extra: sentryExtra,
        tags: Object.keys(sentryTags).length > 0 ? sentryTags : undefined,
      });
    } else {
      Sentry.captureException(new Error(message), {
        extra: sentryExtra,
        tags: Object.keys(sentryTags).length > 0 ? sentryTags : undefined,
      });
    }
  }

  debug(message: string, ...args: unknown[]): void {
    // Log to console.log for debug messages
    // eslint-disable-next-line no-console
    console.log(message, ...args);
  }

  setUser(user: UserContext | null): void {
    Sentry.setUser(user);
  }

  clearUser(): void {
    Sentry.setUser(null);
  }
}

// Create a default logger instance for fallback use
const defaultLoggerInstance = new ActionLogger();

// Logger context
const LoggerContext = createContext<Logger>(defaultLoggerInstance);

export const LoggerProvider: React.FC<{
  children: ReactNode;
  logger?: Logger;
}> = ({ children, logger }) => {
  const loggerValue = useMemo(() => logger ?? defaultLoggerInstance, [logger]);

  return (
    <LoggerContext.Provider value={loggerValue}>
      {children}
    </LoggerContext.Provider>
  );
};

export const useLogger = (): Logger => {
  return useContext(LoggerContext);
};
